import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
    Card,
    Typography,
    LinearProgress,
    Select,
    Option,
    List,
    ListItem,
    ListDivider,
    Sheet,
    Button
} from '@mui/joy';

import createApi from '../api';
import { categoryEmoji, monthName } from '../categoryMeta';

const MonthlyCategories = forwardRef(({ initDataRaw }, ref) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isTotalVisible, setIsTotalVisible] = useState(false);

    // Вычисляем общую сумму трат
    const totalAmount = categories.reduce((sum, category) => sum + Number(category.total), 0);

    const fetchMonthlyCategories = useCallback(async () => {
        const api = createApi(initDataRaw);
        try {
            setLoading(true);
            const response = await api.get('/api/expenses/monthly', {
                params: { month: selectedMonth, year: selectedYear }
            });
            setCategories(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [initDataRaw, selectedMonth, selectedYear]);

    // Expose refresh method to parent
    useImperativeHandle(ref, () => ({
        refresh: fetchMonthlyCategories
    }));

    useEffect(() => {
        fetchMonthlyCategories();
    }, [fetchMonthlyCategories]);

    if (loading) return <LinearProgress />;
    if (error) return <Typography color="danger">{error}</Typography>;

    return (
        <Card variant="outlined">
            <Typography level="h4" component="div" sx={{ mb: 2 }}>
                Траты за месяц

                {/* Спойлер с общей суммой */}
                <Button
                    variant="plain"
                    onClick={() => setIsTotalVisible(!isTotalVisible)}
                    sx={{
                        ml: 1,
                        '&:hover': { backgroundColor: 'transparent' },
                        cursor: 'pointer'
                    }}
                >
                    {isTotalVisible ? (
                        <Typography level="body-lg">
                            {totalAmount.toFixed(2)} руб.
                        </Typography>
                    ) : (
                        <Typography
                            level="body-sm"
                            sx={{ textDecoration: 'underline dotted' }}
                        >
                            [показать сумму]
                        </Typography>
                    )}
                </Button>
            </Typography>

            <Sheet sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Select
                    value={selectedMonth}
                    onChange={(e, value) => setSelectedMonth(value)}
                    size="sm"
                    sx={{ minWidth: 120 }}
                >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <Option key={month} value={month}>
                            {monthName(month)}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={selectedYear}
                    onChange={(e, value) => setSelectedYear(value)}
                    size="sm"
                    sx={{ minWidth: 120 }}
                >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <Option key={year} value={year}>
                            {year}
                        </Option>
                    ))}
                </Select>
            </Sheet>

            <List variant="outlined">
                {categories.map((category, index) => (
                    <React.Fragment key={category.category}>
                        <ListItem>
                            <Typography fontWeight="lg">
                                <span aria-hidden="true">{categoryEmoji(category.category)}</span> {category.category}
                            </Typography>
                            <Typography level="body-sm" sx={{ ml: 'auto' }}>
                                {Number(category.total).toFixed(2)} руб.
                            </Typography>
                        </ListItem>
                        {index < categories.length - 1 && <ListDivider />}
                    </React.Fragment>
                ))}
            </List>
        </Card>
    );
});

export default MonthlyCategories;